import { useId, useState, type FormEvent } from 'react';
import {
  formatEuro,
  mandatoryOfferFields,
  offerFieldLabels,
  parseAmountInput,
  type ConfirmOfferBody,
  type OfferFieldKey,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { confirmOffer, type OfferUploadRow } from './api';
import { parseDecimalInput } from './form';
import styles from './financier.module.css';

/**
 * The offer fields of „Angebot prüfen" (06) and „Eckdaten selbst eintragen" (10): German decimals, euro amounts and
 * days as typed, parsed into the `confirm` body (FI12: bank, Sollzins, Zinsbindung, Gültig bis are mandatory).
 */

type FieldKind = 'text' | 'money' | 'percent' | 'years' | 'date';

const kinds: Record<OfferFieldKey, FieldKind> = {
  bank: 'text',
  loanCents: 'money',
  nominalRatePct: 'percent',
  effectiveRatePct: 'percent',
  fixedInterestYears: 'years',
  installmentCents: 'money',
  repaymentPct: 'percent',
  expiresOn: 'date',
  availableFromOn: 'date',
  forwardPremiumPct: 'percent',
};

/** The eight fields of the manual entry (Finanzierer 10), in the frame's order. */
export const manualFieldKeys: readonly OfferFieldKey[] = [
  'bank',
  'loanCents',
  'nominalRatePct',
  'effectiveRatePct',
  'fixedInterestYears',
  'installmentCents',
  'repaymentPct',
  'expiresOn',
];

export type OfferFormValues = Partial<Record<OfferFieldKey, string>>;
export type OfferFormErrors = Partial<Record<OfferFieldKey, string>>;

const NUMBER_ERROR = 'Bitte eine Zahl angeben.';

function parseMoney(text: string): number | null | 'invalid' {
  const cleaned = text.replace(/mtl\.?|monatlich/gi, '').trim();
  if (cleaned === '') return null;
  const cents = parseAmountInput(cleaned);
  return cents === null ? 'invalid' : cents;
}

/** Whether the mandatory fields carry a value (the „Übernehmen" gate of Finanzierer 10). */
export function mandatoryFilled(values: OfferFormValues): boolean {
  return mandatoryOfferFields.every((key) => (values[key] ?? '').trim() !== '');
}

/** Parses the typed values into the confirm body, or returns the client-side field errors. */
export function buildConfirmBody(
  values: OfferFormValues,
  keys: readonly OfferFieldKey[],
): { body: ConfirmOfferBody } | { errors: OfferFormErrors } {
  const errors: OfferFormErrors = {};
  const parsed: Partial<Record<OfferFieldKey, string | number | null>> = {};
  for (const key of keys) {
    const text = (values[key] ?? '').trim();
    const kind = kinds[key];
    if (text === '') {
      parsed[key] = null;
      continue;
    }
    if (kind === 'text' || kind === 'date') {
      parsed[key] = text;
    } else if (kind === 'money') {
      const cents = parseMoney(text);
      if (cents === 'invalid') errors[key] = 'Bitte einen Betrag angeben.';
      else parsed[key] = cents;
    } else {
      const value = parseDecimalInput(text);
      if (value === null) parsed[key] = null;
      else if (Number.isNaN(value)) errors[key] = NUMBER_ERROR;
      else parsed[key] = value;
    }
  }
  for (const key of mandatoryOfferFields) {
    if (keys.includes(key) && (parsed[key] ?? null) === null && !errors[key]) errors[key] = 'Pflichtangabe.';
  }
  if (Object.keys(errors).length > 0) return { errors };
  const body: ConfirmOfferBody = {
    bank: String(parsed.bank),
    nominalRatePct: Number(parsed.nominalRatePct),
    fixedInterestYears: Number(parsed.fixedInterestYears),
    expiresOn: String(parsed.expiresOn),
  };
  const optional = (key: OfferFieldKey) => (keys.includes(key) ? (parsed[key] ?? null) : undefined);
  const loan = optional('loanCents');
  if (loan !== undefined) body.loanCents = loan === null ? null : Number(loan);
  const effective = optional('effectiveRatePct');
  if (effective !== undefined) body.effectiveRatePct = effective === null ? null : Number(effective);
  const installment = optional('installmentCents');
  if (installment !== undefined) body.installmentCents = installment === null ? null : Number(installment);
  const repayment = optional('repaymentPct');
  if (repayment !== undefined) body.repaymentPct = repayment === null ? null : Number(repayment);
  const available = optional('availableFromOn');
  if (available !== undefined) body.availableFromOn = available === null ? null : String(available);
  const premium = optional('forwardPremiumPct');
  if (premium !== undefined) body.forwardPremiumPct = premium === null ? null : Number(premium);
  return { body };
}

/** One field of the frames: muted 12 px label, 40 px box with the value, optional tag („erkannt"/„unsicher") and hint. */
export function OfferFieldInput({
  fieldKey,
  label,
  value,
  onChange,
  tag,
  hint,
  error,
  disabled,
}: {
  fieldKey: OfferFieldKey;
  label: string;
  value: string;
  onChange: (value: string) => void;
  tag?: 'erkannt' | 'unsicher' | null | undefined;
  hint?: string | null | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
}) {
  const id = useId();
  const kind = kinds[fieldKey];
  const unsure = tag === 'unsicher';
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ');
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      <span
        className={clsx(styles.fieldBox, unsure && styles.fieldBoxUnsure, error && styles.fieldBoxInvalid)}
      >
        <input
          id={id}
          className={styles.fieldInput}
          type={kind === 'date' ? 'date' : 'text'}
          inputMode={kind === 'text' || kind === 'date' ? undefined : 'decimal'}
          value={value}
          disabled={disabled}
          required={mandatoryOfferFields.includes(fieldKey)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          onChange={(event) => onChange(event.target.value)}
          onClick={(event) => {
            if (kind !== 'date') return;
            try {
              event.currentTarget.showPicker();
            } catch {
              event.currentTarget.focus();
            }
          }}
        />
        {tag ? <span className={clsx(styles.fieldTag, unsure && styles.fieldTagUnsure)}>{tag}</span> : null}
      </span>
      {hint ? (
        <span id={`${id}-hint`} className={styles.fieldHintWarn}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={`${id}-error`} className={styles.fieldError} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/** Server field errors of `confirm` (keys = body keys) → form errors. */
export function serverFieldErrors(error: unknown): OfferFormErrors | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const fields = error.fields;
  if (Object.keys(fields).length === 0) return null;
  return fields;
}

export interface ManualOfferFormProps {
  financingId: string;
  upload: OfferUploadRow;
  onCancel: () => void;
  onDone: (stageMoved: boolean) => void;
}

/** Finanzierer 10 „Eckdaten selbst eintragen" (4 columns, 8 fields) → `POST …/offers/:offerId/confirm`. */
export function ManualOfferForm({ financingId, upload, onCancel, onDone }: ManualOfferFormProps) {
  const formId = useId();
  const [values, setValues] = useState<OfferFormValues>(() => ({
    bank: upload.prefill.bank ?? '',
    loanCents: upload.prefill.loanCents === null ? '' : formatEuro(upload.prefill.loanCents),
  }));
  const [errors, setErrors] = useState<OfferFormErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const result = buildConfirmBody(values, manualFieldKeys);
    if ('errors' in result) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setBanner(null);
    setPending(true);
    try {
      const outcome = await confirmOffer(financingId, upload.offerId, result.body);
      onDone(outcome.stageMoved);
    } catch (error) {
      const fields = serverFieldErrors(error);
      if (fields) setErrors(fields);
      else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className={styles.card} aria-labelledby={`${formId}-title`}>
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <div id={`${formId}-title`} className={styles.cardTitle}>
            Eckdaten selbst eintragen
          </div>
          <div className={styles.cardSubtitle}>
            {upload.fileName} · Das PDF bleibt als Nachweis am Angebot.
          </div>
        </div>
      </div>
      <form id={formId} onSubmit={submit} noValidate>
        {banner ? (
          <Banner tone="danger" style={{ marginTop: 14 }}>
            {banner}
          </Banner>
        ) : null}
        <div className={styles.fieldsFour}>
          {manualFieldKeys.map((key) => (
            <OfferFieldInput
              key={key}
              fieldKey={key}
              label={offerFieldLabels[key]}
              value={values[key] ?? ''}
              error={errors[key]}
              disabled={pending}
              onChange={(value) => setValues((current) => ({ ...current, [key]: value }))}
            />
          ))}
        </div>
        <div className={styles.cardFooterPlain}>
          <span className={styles.footNote}>Sollzins, Zinsbindung und Gültigkeit sind Pflicht</span>
          <span className={styles.footActions}>
            <Button variant="secondary" onClick={onCancel} disabled={pending}>
              Abbrechen
            </Button>
            <Button type="submit" loading={pending} disabled={!mandatoryFilled(values) || pending}>
              Übernehmen
            </Button>
          </span>
        </div>
      </form>
    </section>
  );
}
